/**
 * ReplyPane — inline reply / forward composer with rich text.
 * Mode: 'reply' | 'reply-all' | 'forward'
 */

import { useState, useEffect, useRef } from 'react';
import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query';
import { toast } from 'sonner';
import { PaperPlaneTilt, X, Paperclip } from '@phosphor-icons/react';
import { replyToEmail, forwardEmail } from '@/lib/inboxApi';
import { fetchDefaultSignatures } from '@/lib/inboxSignaturesApi';
import { RichTextEditor } from './RichTextEditor';

/**
 * The minimal email shape the composer needs. Satisfied by both EmailDetail (single-email
 * view, InboxThreadPane) AND a MailConversation email (thread reading pane), so Reply /
 * Reply-All / Forward work identically from either surface.
 */
export interface ReplyableEmail {
  id: string;
  fromAddress: string;
  toAddresses: string[];
  ccAddresses: string[];
  subject: string;
  bodyText: string | null;
  bodyHtml: string | null;
  sentAt: string | null;
  receivedAt: string | null;
}

interface Props {
  email: ReplyableEmail;
  mode: 'reply' | 'reply-all' | 'forward';
  onClose: () => void;
  onSent?: () => void;
}

export function ReplyPane({ email, mode, onClose, onSent }: Props) {
  const queryClient = useQueryClient();
  const [to, setTo] = useState(() => {
    if (mode === 'forward') return '';
    return email.fromAddress;
  });
  const [cc, setCc] = useState('');
  const [showCc, setShowCc] = useState(mode === 'reply-all');
  const [bodyHtml, setBodyHtml] = useState('');
  const [attachments, setAttachments] = useState<File[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const MAX_FILE_BYTES = 25 * 1024 * 1024;

  function handleFilesPicked(fileList: FileList | null) {
    if (!fileList?.length) return;
    const incoming = Array.from(fileList);
    const tooBig = incoming.filter((f) => f.size > MAX_FILE_BYTES);
    if (tooBig.length) toast.error(`${tooBig.map((f) => f.name).join(', ')} exceeds the 25 MB limit`);
    setAttachments((prev) => [...prev, ...incoming.filter((f) => f.size <= MAX_FILE_BYTES)]);
    if (fileInputRef.current) fileInputRef.current.value = '';
  }

  function removeAttachment(index: number) {
    setAttachments((prev) => prev.filter((_, i) => i !== index));
  }

  function formatBytes(bytes: number): string {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }

  const { data: defaultSigs, isLoading: sigsLoading } = useQuery({
    queryKey: ['inbox-signature-defaults'],
    queryFn: fetchDefaultSignatures,
    staleTime: 300_000,
    refetchOnWindowFocus: false,
  });

  const sigHtml = defaultSigs?.forReply?.body
    ? `<p><br></p><div style="border-top:1px solid var(--border-default);margin-top:8px;padding-top:8px;color:#6b7280">${defaultSigs.forReply.body}</div>`
    : '';

  useEffect(() => {
    if (mode === 'reply-all') {
      setCc(email.ccAddresses.join(', '));
    }
  }, [mode]);

  const label = mode === 'forward' ? 'Forward' : mode === 'reply-all' ? 'Reply All' : 'Reply';
  const subjectPrefix = mode === 'forward' ? 'Fwd: ' : 'Re: ';
  const displaySubject = email.subject.startsWith(subjectPrefix)
    ? email.subject
    : `${subjectPrefix}${email.subject}`;

  const mutation = useMutation({
    mutationFn: () => {
      if (mode === 'forward') {
        const toList = to.split(/[,;]+/).map((s) => s.trim()).filter(Boolean);
        if (!toList.length) throw new Error('Add at least one recipient');
        return forwardEmail(email.id, {
          to: toList,
          bodyHtml: bodyHtml || undefined,
          bodyText: bodyHtml ? bodyHtml.replace(/<[^>]*>/g, ' ').trim() : undefined,
          attachments: attachments.length ? attachments : undefined,
        });
      }
      return replyToEmail(email.id, {
        replyAll: mode === 'reply-all',
        bodyHtml: bodyHtml || undefined,
        bodyText: bodyHtml ? bodyHtml.replace(/<[^>]*>/g, ' ').trim() : undefined,
        attachments: attachments.length ? attachments : undefined,
      });
    },
    onSuccess: () => {
      toast.success(`${label} sent`);
      queryClient.invalidateQueries({ queryKey: ['inbox-emails'] });
      queryClient.invalidateQueries({ queryKey: ['folder-counts'] });
      onSent?.();
      onClose();
    },
    onError: (err: Error) => {
      toast.error(err.message || `Failed to send ${label.toLowerCase()}`);
    },
  });

  const inputClass = 'flex h-8 w-full rounded border border-border bg-transparent px-2 text-sm text-foreground placeholder:text-muted-foreground focus:border-brand-500 focus:outline-none';

  return (
    <div style={{
      marginTop: 16, borderRadius: 10,
      border: '1px solid var(--border-strong)',
      background: 'var(--surface-card)',
      boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
      overflow: 'hidden',
    }}>
      {/* Header */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '8px 14px', borderBottom: '1px solid #f0f0f0',
        background: '#fafafa',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontFamily: "'IBM Plex Sans', sans-serif", fontSize: 13, fontWeight: 600, color: 'var(--text-primary)' }}>
            {label}
          </span>
          <span style={{ fontFamily: "'IBM Plex Sans', sans-serif", fontSize: 12, color: 'var(--text-subtle)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 300 }}>
            {displaySubject}
          </span>
        </div>
        <button
          onClick={onClose}
          style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-subtle)', display: 'flex' }}
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      {/* To field */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '6px 14px', borderBottom: '1px solid #f0f0f0' }}>
        <span style={{ width: 32, fontSize: 12, color: 'var(--text-subtle)', flexShrink: 0 }}>To</span>
        {mode === 'forward' ? (
          <input
            value={to}
            onChange={(e) => setTo(e.target.value)}
            autoFocus
            placeholder="recipient@example.com"
            className={inputClass}
          />
        ) : (
          <span style={{ fontFamily: "'IBM Plex Sans', sans-serif", fontSize: 13, color: 'var(--text-primary)' }}>
            {mode === 'reply-all'
              ? [email.fromAddress, ...email.toAddresses].join(', ')
              : email.fromAddress}
          </span>
        )}
        {!showCc && (
          <button onClick={() => setShowCc(true)} style={{ fontSize: 11, color: 'var(--text-subtle)', background: 'none', border: 'none', cursor: 'pointer', flexShrink: 0 }}>
            Cc
          </button>
        )}
      </div>

      {showCc && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '6px 14px', borderBottom: '1px solid #f0f0f0' }}>
          <span style={{ width: 32, fontSize: 12, color: 'var(--text-subtle)', flexShrink: 0 }}>Cc</span>
          <input value={cc} onChange={(e) => setCc(e.target.value)} placeholder="cc@example.com" className={inputClass} />
        </div>
      )}

      {/* Rich text compose area */}
      <div style={{ minHeight: 140, display: 'flex', flexDirection: 'column' }}>
        {!sigsLoading && (
          <RichTextEditor
            placeholder={`Write your ${label.toLowerCase()}…`}
            defaultValue={sigHtml}
            onHtmlChange={setBodyHtml}
            minHeight={120}
            autoFocus={mode !== 'forward'}
          />
        )}
      </div>

      {/* Quoted original */}
      <div style={{ borderTop: '1px solid #f0f0f0', padding: '10px 14px' }}>
        <div style={{ fontSize: 11, color: 'var(--text-subtle)', marginBottom: 6 }}>
          On {new Date(email.sentAt ?? email.receivedAt ?? '').toLocaleString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}, {email.fromAddress} wrote:
        </div>
        <div style={{
          borderLeft: '3px solid #d1d5db', paddingLeft: 10,
          fontSize: 12, color: 'var(--text-subtle)', lineHeight: 1.6,
          maxHeight: 80, overflow: 'hidden',
          fontFamily: "'IBM Plex Sans', sans-serif",
        }}>
          {(email.bodyText ?? (email.bodyHtml ?? '').replace(/<[^>]+>/g, ' ')).slice(0, 280)}…
        </div>
      </div>

      {/* Attached files */}
      {attachments.length > 0 && (
        <div style={{
          display: 'flex', flexWrap: 'wrap', gap: 6, padding: '8px 14px',
          borderTop: '1px solid #f0f0f0', background: 'var(--surface-card)',
        }}>
          {attachments.map((file, i) => (
            <div
              key={`${file.name}-${i}`}
              style={{
                display: 'flex', alignItems: 'center', gap: 6,
                background: 'var(--surface-hover)', border: '1px solid var(--border-default)',
                borderRadius: 6, padding: '4px 8px', maxWidth: 220,
              }}
            >
              <Paperclip size={13} color="#6b7280" style={{ flexShrink: 0 }} />
              <span style={{
                fontFamily: "'IBM Plex Sans', sans-serif", fontSize: 12, color: 'var(--text-primary)',
                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              }}>
                {file.name}
              </span>
              <span style={{ fontFamily: "'IBM Plex Sans', sans-serif", fontSize: 11, color: 'var(--text-subtle)', flexShrink: 0 }}>
                {formatBytes(file.size)}
              </span>
              <button
                onClick={() => removeAttachment(i)}
                title="Remove attachment"
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-subtle)', display: 'flex', flexShrink: 0, padding: 0 }}
              >
                <X className="h-3 w-3" />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Send bar */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '8px 14px', borderTop: '1px solid #f0f0f0', background: '#fafafa',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <button
            onClick={() => mutation.mutate()}
            disabled={mutation.isPending}
            style={{
              display: 'flex', alignItems: 'center', gap: 6,
              padding: '7px 16px', borderRadius: 20,
              background: '#2563eb', color: 'white',
              border: 'none', cursor: 'pointer',
              fontFamily: "'IBM Plex Sans', sans-serif",
              fontSize: 13, fontWeight: 600,
              opacity: mutation.isPending ? 0.6 : 1,
            }}
          >
            <PaperPlaneTilt className="h-4 w-4" weight="fill" />
            {mutation.isPending ? 'Sending…' : `Send ${label}`}
          </button>

          {/* Attach file — real picker */}
          <input
            ref={fileInputRef}
            type="file"
            multiple
            style={{ display: 'none' }}
            onChange={(e) => handleFilesPicked(e.target.files)}
          />
          <button
            onClick={() => fileInputRef.current?.click()}
            title="Attach file"
            style={{
              width: 32, height: 32, borderRadius: 6, border: 'none',
              background: attachments.length ? '#eff6ff' : 'none', cursor: 'pointer',
              color: attachments.length ? '#2563eb' : 'var(--text-muted)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
          >
            <Paperclip size={16} />
          </button>
        </div>
        <button
          onClick={onClose}
          style={{ fontFamily: "'IBM Plex Sans', sans-serif", fontSize: 12, color: 'var(--text-subtle)', background: 'none', border: 'none', cursor: 'pointer' }}
        >
          Discard
        </button>
      </div>
    </div>
  );
}
